import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import {
  fetchConversations,
  fetchModelCatalog,
  selectConversation,
  sendMessage,
  deleteConversation,
  uploadFile,
  setInputValue,
  setModel,
  setModelOverride,
  removeAttachedFile,
  startNewConversation,
  ConversationListItem,
  SendMessageRejection,
} from "../../../store/aiChatSlice";
import { UsageStatus } from "../../../services/mcpUsageService";
import { ChatModelId } from "../../../services/mcpService";
import useChatScroll from "./useChatScroll";
import useEnterToSend from "./useEnterToSend";
import { usePostHogCapture } from "../../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../../utils/posthogEvents";
import { AgentCommand, filterAgentCommands } from "../../../utils/agentCommands";
import { markStepComplete } from "../../../store/clubChecklistSlice";

interface UseAiChatOptions {
  onHistoryClose?: () => void;
  userId?: string | null;
  isAdmin?: boolean;
  onUsageRecorded?: () => void;
  onLimitExceeded?: (usageStatus: UsageStatus) => void;
  onNoSubscription?: (usageStatus: UsageStatus) => void;
  persona?: string;
  // Returns a prompt override to send to MCP silently — leave inputValue for display/storage
  onBeforeSend?: (inputValue: string) => string | undefined;
  // Overrides the default textarea focus (e.g. the analyst page focuses its Lexical editor)
  focusInput?: () => void;
  // Decides per-send whether to append AI onboarding guidance. Receives the message text and whether this is the user's first-ever message / an established user, so the caller can gate on tour state, new-user intent, and the once-per-user flag.
  resolveOnboarding?: (ctx: {
    text: string;
    isFirstEverMessage: boolean;
    isEstablishedUser: boolean;
    isFirstMessageInConversation: boolean;
  }) => boolean;
  // Called after an onboarding-flagged message sends successfully, to persist the once-only flag.
  onOnboardingSent?: () => void;
}

const useAiChat = (options: UseAiChatOptions = {}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { capture } = usePostHogCapture();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [agentMenuOpen, setAgentMenuOpen] = useState(false);
  const [filteredCommands, setFilteredCommands] = useState<AgentCommand[]>([]);
  const [activeAgentCommand, setActiveAgentCommand] = useState<string | null>(null);

  // Select state from Redux
  const {
    messages,
    messagesLoading,
    currentConversation,
    conversations,
    conversationsLoading,
    inputValue,
    model,
    modelCatalog,
    modelOverride,
    attachedFiles,
    uploadingCount,
    isSending,
    sendError,
    activeConversationTokens,
    pendingConversationId,
  } = useSelector((state: RootState) => state.aiChat);

  // Custom hooks
  const lastMessageIsUser = messages[messages.length - 1]?.isUser ?? false;
  useChatScroll(messagesEndRef, [messages, isSending], lastMessageIsUser);

  // Load conversations on mount
  useEffect(() => {
    dispatch(fetchConversations(20));
  }, [dispatch]);

  // Model options are server-owned — fetch once so the picker has something to render
  useEffect(() => {
    if (!modelCatalog) dispatch(fetchModelCatalog());
  }, [dispatch, modelCatalog]);

  // Restore cached message from sessionStorage (from no_subscription rejection)
  useEffect(() => {
    const cachedMessage = sessionStorage.getItem('pendingChatMessage');
    if (cachedMessage) {
      dispatch(setInputValue(cachedMessage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - intentionally ignoring dispatch

  // Handlers
  const handleConversationSelect = useCallback(
    async (conversation: ConversationListItem) => {
      await dispatch(selectConversation(conversation.id));
      options.onHistoryClose?.();
    },
    [dispatch, options]
  );

  const handleNewConversation = useCallback(() => {
    dispatch(startNewConversation());
    options.onHistoryClose?.();
  }, [dispatch, options]);

  const handleSend = useCallback(async () => {
    // promptOverride carries extra context (e.g. asset mentions) sent silently to MCP inputValue stays as-is for display and storage
    const promptOverride = options.onBeforeSend ? options.onBeforeSend(inputValue) : undefined;

    // Capture message details before sending (inputValue gets cleared after send)
    const messageText = inputValue;
    const messageLength = inputValue.length;
    const hasAttachments = attachedFiles.length > 0;
    const attachmentCount = attachedFiles.length;

    // Resolve onboarding from live chat history: first-ever = no prior conversations or messages; established = more than one prior conversation. Ignore the optimistic pending placeholder. Require a confirmed successful fetch — idle/pending/failed all count as established so a returning user whose fetch is in-flight or errored is never misread as new and falsely onboarded.
    const conversationsReady = conversationsLoading === "success";
    const priorMessages = messages.filter((m) => !m.id.endsWith("-pending"));
    const isFirstMessageInConversation = priorMessages.length === 0;
    const isFirstEverMessage =
      conversationsReady && conversations.length === 0 && isFirstMessageInConversation;
    const isEstablishedUser = !conversationsReady || conversations.length > 1;
    const onboarding =
      options.resolveOnboarding?.({
        text: inputValue,
        isFirstEverMessage,
        isEstablishedUser,
        isFirstMessageInConversation,
      }) ?? false;

    // Pass the current input values to the thunk to avoid race conditions
    const result = await dispatch(
      sendMessage({
        inputValue,
        attachedFilesInput: attachedFiles,
        userId: options.userId ?? null,
        persona: options.persona,
        isAdmin: options.isAdmin,
        promptOverride,
        onboarding,
      })
    );

    // Track message sent with result - captured after send to include success/failure status This allows filtering analytics by successful sends vs failed attempts (e.g., limit_exceeded)
    const isSuccess = sendMessage.fulfilled.match(result);
    const rejectionPayload = sendMessage.rejected.match(result)
      ? (result.payload as SendMessageRejection | undefined)
      : null;

    capture(POSTHOG_EVENTS.CHAT_MESSAGE_SENT, {
      message_text: messageText,
      message_length: messageLength,
      has_attachments: hasAttachments,
      attachment_count: attachmentCount,
      send_success: isSuccess,
      failure_reason: rejectionPayload?.type ?? null, // e.g., "limit_exceeded", "empty_message", etc.
    });

    // Handle callbacks based on action result (keeps thunk args serializable)
    if (isSuccess) {
      // Clear cached message on successful send
      sessionStorage.removeItem('pendingChatMessage');
      // Mark checklist step 1 complete (ask a question)
      dispatch(markStepComplete(1));
      // Persist the once-per-user onboarding flag so guidance never re-sends
      if (onboarding) options.onOnboardingSent?.();
    } else if (rejectionPayload?.type === "no_subscription") {
      // Cache the message so it persists after they subscribe
      sessionStorage.setItem('pendingChatMessage', messageText);
      options.onNoSubscription?.(rejectionPayload.usageStatus);
    } else if (rejectionPayload?.type === "limit_exceeded") {
      options.onLimitExceeded?.(rejectionPayload.usageStatus);
    }
  }, [dispatch, options, inputValue, attachedFiles, messages, conversations, conversationsLoading, capture]);

  const handleInputChange = useCallback(
    (value: string) => {
      dispatch(setInputValue(value));
      const commands = filterAgentCommands(value);
      setFilteredCommands(commands);
      setAgentMenuOpen(commands.length > 0 && !value.includes(' '));
      if (!value.startsWith('/')) setActiveAgentCommand(null);
    },
    [dispatch]
  );

  const handleAgentCommandSelect = useCallback(
    (cmd: AgentCommand) => {
      dispatch(setInputValue(`/${cmd.command} `));
      setActiveAgentCommand(cmd.command);
      setAgentMenuOpen(false);
      if (options.focusInput) {
        options.focusInput();
      } else {
        inputRef.current?.focus();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, options.focusInput]
  );

  const handleModelChange = useCallback(
    (modelId: ChatModelId) => {
      dispatch(setModel(modelId));
    },
    [dispatch]
  );

  const handleModelOverrideChange = useCallback(
    (modelId: ChatModelId) => {
      dispatch(setModelOverride(modelId));
    },
    [dispatch]
  );

  const handleFileSelect = useCallback(
    async (files: FileList) => {
      // Track file attachments
      Array.from(files).forEach((file) => {
        capture(POSTHOG_EVENTS.CHAT_FILE_ATTACHED, {
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
        });
      });

      // Use existing conversation ID or the pre-generated pending UUID for new chats
      const conversationId = currentConversation?.id ?? pendingConversationId;

      // Upload files in parallel for better performance
      const uploadPromises = Array.from(files).map((file) =>
        dispatch(uploadFile({ file, conversationId }))
      );
      await Promise.all(uploadPromises);
    },
    [dispatch, capture, currentConversation, pendingConversationId]
  );

  const handleRemoveFile = useCallback(
    (uri: string) => {
      dispatch(removeAttachedFile(uri));
    },
    [dispatch]
  );

  const handleDeleteConversation = useCallback(
    async (conversationId: string) => {
      await dispatch(deleteConversation(conversationId));
    },
    [dispatch]
  );

  // Re-send a prompt for a failed (status "error") AI message. Sends as a fresh message, which creates a new pending placeholder; the prior error bubble stays in the history.
  const handleRetry = useCallback(
    (promptText: string) => {
      if (!promptText?.trim()) return;
      dispatch(
        sendMessage({
          inputValue: promptText,
          attachedFilesInput: [],
          userId: options.userId ?? null,
          persona: options.persona,
          isAdmin: options.isAdmin,
        })
      );
    },
    [dispatch, options.userId, options.persona, options.isAdmin]
  );

  const handleKeyDown = useEnterToSend(handleSend);

  return {
    // State
    inputValue,
    messages,
    isLoading: isSending,
    error: sendError,
    currentConversation,
    isFetchingConversation: messagesLoading === "pending",
    conversations,
    isLoadingConversations: conversationsLoading === "pending",
    model,
    modelOptions: modelCatalog?.models ?? [],
    modelSurfacePinned: modelCatalog?.surfacePinned ?? false,
    modelOverride,
    attachedFiles,
    isUploading: uploadingCount > 0,
    activeConversationTokens,

    // Refs
    messagesEndRef,
    inputRef,

    // Handlers
    setInputValue: handleInputChange,
    setModel: handleModelChange,
    setModelOverride: handleModelOverrideChange,
    handleConversationSelect,
    handleNewConversation,
    handleSend,
    handleRetry,
    handleKeyDown,
    handleFileSelect,
    handleRemoveFile,
    handleDeleteConversation,

    // Agent command menu
    agentMenuOpen,
    filteredCommands,
    activeAgentCommand,
    handleAgentCommandSelect,
  };
};

export default useAiChat;
